// API Service - handles all fetch calls to the backend

import type {
  SessionMetrics,
  DurationDistribution,
  TimelineDataPoint,
  ProductMention,
  NoProductEvent,
  Alternative,
  Session,
  MarketingOverview,
  ConversionComparison,
  FunnelData,
  SessionTimeline
} from './types';

// Token storage key
const TOKEN_KEY = 'analytics_auth_token';

class ApiService {
  private baseUrl = '';

  // Get stored auth token
  private getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  // Store auth token
  setToken(token: string): void {
    localStorage.setItem(TOKEN_KEY, token);
  }

  // Clear auth token
  clearToken(): void {
    localStorage.removeItem(TOKEN_KEY);
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  private buildUrl(endpoint: string, params?: Record<string, string | number | boolean | undefined>): string {
    const url = new URL(endpoint, window.location.origin);
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          url.searchParams.append(key, String(value));
        }
      });
    }
    return url.toString();
  }

  private async fetch<T>(endpoint: string, params?: Record<string, string | number | boolean | undefined>): Promise<T> {
    const url = this.buildUrl(endpoint, params);
    const token = this.getToken();
    
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    try {
      const response = await fetch(url, { headers });
      
      // Handle 401 - redirect to login
      if (response.status === 401) {
        this.clearToken();
        window.dispatchEvent(new CustomEvent('auth:logout'));
        throw new Error('Session expired. Please login again.');
      }
      
      if (!response.ok) {
        // Try to get error message from response
        let errorMessage = `${response.status} ${response.statusText}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch {
          // Response is not JSON, use status text
        }
        throw new Error(`API error: ${errorMessage}`);
      }
      
      // Check content type to avoid JSON parse errors on HTML error pages
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        console.warn(`Unexpected content type: ${contentType} for ${endpoint}`);
        return {} as T;
      }
      
      return response.json();
    } catch (error: any) {
      // Network errors, timeouts, etc.
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        console.error(`Network error fetching ${endpoint}:`, error);
        throw new Error('Network error - please check your connection');
      }
      throw error;
    }
  }

  // Clients
  async getClients(): Promise<{ clients: string[] }> {
    return this.fetch('/api/clients');
  }

  // Server config (default client ID from env)
  async getConfig(): Promise<{ clientId: string; version: string }> {
    return this.fetch('/api/config');
  }

  // Session metrics
  async getSessionMetrics(clientId?: string, startDate?: string, endDate?: string): Promise<{ metrics: SessionMetrics }> {
    return this.fetch('/api/sessions/metrics/overview', { clientId, startDate, endDate });
  }

  async getDurationDistribution(clientId?: string, startDate?: string, endDate?: string): Promise<{ distribution: DurationDistribution[] }> {
    return this.fetch('/api/sessions/metrics/duration-distribution', { clientId, startDate, endDate });
  }

  async getMessagesDistribution(clientId?: string, startDate?: string, endDate?: string): Promise<{ distribution: any[] }> {
    return this.fetch('/api/sessions/metrics/messages-distribution', { clientId, startDate, endDate });
  }

  async getSessionTimeline(clientId?: string, startDate?: string, endDate?: string): Promise<{ timeline: TimelineDataPoint[] }> {
    return this.fetch('/api/sessions/metrics/timeline', { clientId, startDate, endDate });
  }

  async getSessions(params: {
    clientId?: string;
    limit?: number;
    offset?: number;
    startDate?: string;
    endDate?: string;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<{ sessions: Session[]; total: number }> {
    return this.fetch('/api/sessions', params);
  }

  async getSessionDetails(sessionId: string): Promise<any> {
    return this.fetch(`/api/sessions/${encodeURIComponent(sessionId)}`);
  }

  async getSessionTimelineById(sessionId: string): Promise<SessionTimeline> {
    return this.fetch(`/api/sessions/${encodeURIComponent(sessionId)}/timeline`);
  }

  // Products
  async getProductMentionsByMonth(clientId?: string): Promise<ProductMention[]> {
    return this.fetch('/api/products/mentions-by-month', { clientId });
  }

  async getProductBubbleData(clientId?: string): Promise<{ bubbles: any[] }> {
    return this.fetch('/api/products/bubble-data', { clientId });
  }

  async getNoProducts(params: {
    clientId?: string;
    limit?: number;
    offset?: number;
    startDate?: string;
    endDate?: string;
    searchQuery?: string;
  }): Promise<{ events: NoProductEvent[]; total: number }> {
    return this.fetch('/api/products/no-products', params);
  }

  async getAlternatives(params: {
    clientId?: string;
    limit?: number;
    offset?: number;
    startDate?: string;
    endDate?: string;
    searchedProduct?: string;
    suggestedProduct?: string;
    category?: string;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<{ alternatives: Alternative[]; total: number }> {
    return this.fetch('/api/products/alternatives', params);
  }

  async getProducts(params: {
    clientId?: string;
    limit?: number;
    offset?: number;
    category?: string;
    inStock?: boolean;
    search?: string;
    startDate?: string;
    endDate?: string;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<{ products: any[]; total: number }> {
    return this.fetch('/api/products/list', params);
  }

  async getProductsStock(clientId: string, productIds: string[]): Promise<{ stock: Record<string, { inStock: boolean; stockQuantity: number | null; stockStatus: string }> }> {
    const token = this.getToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch('/api/products/stock', {
      method: 'POST',
      headers,
      body: JSON.stringify({ clientId, productIds })
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch product stock: ${response.status}`);
    }
    return response.json();
  }

  async getCategories(params: {
    clientId?: string;
    limit?: number;
    offset?: number;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<{ categories: any[]; total: number }> {
    return this.fetch('/api/products/categories', params);
  }

  async getOutOfStock(params: {
    clientId?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ products: any[]; total: number }> {
    return this.fetch('/api/products/out-of-stock', params);
  }

  // Marketing Intelligence
  async getMarketingOverview(clientId: string): Promise<MarketingOverview> {
    return this.fetch('/api/marketing/overview', { clientId });
  }

  async getConversionComparison(clientId: string): Promise<ConversionComparison> {
    return this.fetch('/api/marketing/conversion-comparison', { clientId });
  }

  async getFunnel(clientId: string): Promise<{ funnel: FunnelData }> {
    return this.fetch('/api/marketing/funnel', { clientId });
  }

  async getTrafficSources(clientId: string): Promise<{ sources: any }> {
    return this.fetch('/api/marketing/traffic-sources', { clientId });
  }

  async getDeviceTypes(clientId: string): Promise<{ devices: any }> {
    return this.fetch('/api/marketing/device-types', { clientId });
  }

  async getRecommendationsStats(clientId: string): Promise<any> {
    return this.fetch('/api/marketing/recommendations/stats', { clientId });
  }

  async getTopRecommendations(clientId: string, limit: number = 10): Promise<{ recommendations: any[] }> {
    return this.fetch('/api/marketing/recommendations/top', { clientId, limit });
  }

  async getOrders(params: {
    clientId: string;
    limit?: number;
    offset?: number;
    startDate?: string;
    endDate?: string;
    attribution?: string;
    orderId?: string;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<{ orders: any[]; total: number }> {
    return this.fetch('/api/marketing/orders', params);
  }

  async getOrderDetails(orderId: string, clientId?: string): Promise<any> {
    return this.fetch(`/api/marketing/orders/${orderId}`, { clientId });
  }

  async syncOrders(clientId: string): Promise<{ success: boolean; stats?: any; error?: string }> {
    const token = this.getToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch('/api/marketing/sync-orders', {
      method: 'POST',
      headers,
      body: JSON.stringify({ clientId })
    });
    return response.json();
  }

  async deleteOrder(orderId: number, clientId: string): Promise<{ success: boolean; error?: string }> {
    const token = this.getToken();
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(`/api/marketing/orders/${orderId}?clientId=${clientId}`, {
      method: 'DELETE',
      headers
    });
    return response.json();
  }

  // Tracker debug
  async getRecentTrackerEvents(clientId?: string, limit: number = 50): Promise<{ events: any[] }> {
    return this.fetch('/api/track/debug', { clientId, limit });
  }

  // ============================================
  // AUTHENTICATION METHODS
  // ============================================

  async login(username: string, password: string): Promise<{ success: boolean; token?: string; error?: string }> {
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
      
      const data = await response.json();
      
      if (response.ok && data.token) {
        this.setToken(data.token);
        return { success: true, token: data.token };
      }
      
      return { success: false, error: data.error || 'Login failed' };
    } catch (error: any) {
      return { success: false, error: error.message || 'Network error' };
    }
  }

  async verifyToken(): Promise<{ valid: boolean; username?: string }> {
    const token = this.getToken();
    if (!token) {
      return { valid: false };
    }
    
    try {
      const response = await fetch('/api/auth/verify', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        this.clearToken();
        return { valid: false };
      }
      
      const data = await response.json();
      if (!data.valid) {
        this.clearToken();
      }
      return data;
    } catch (error) {
      this.clearToken();
      return { valid: false };
    }
  }

  logout(): void {
    this.clearToken();
    window.dispatchEvent(new CustomEvent('auth:logout'));
  }
}

export const api = new ApiService();
